import type {
  NegotiationEntryStateDto,
  NegotiationHistoryEntryDto,
  NegotiationHistoryFilterDto,
  NegotiationHistoryLineRefDto,
  NegotiationLineDto,
} from '@/api/negotiation-client';
import { seniorityLabel } from '@/lib/operational-view-labels';

// =============================================================================
// negotiation-view.ts — Dérivations PURES de l'écran « Négociation » refondu (lot N4).
// =============================================================================
// Spec : `tasks/spec-refonte-negociation-20260917.md` (décisions GATE_HUMAN du 2026-09-17, précision du
// 2026-09-18 sur le modèle de verrouillage), DBU v2.9 §2.5, UX §8.6.
//
// CE QUI VIT ICI : uniquement de l'HABILLAGE et des agrégats d'AFFICHAGE sur les lignes déjà rendues par
// l'API (lot N1). L'écart, l'état, la valeur figée, le tri et le périmètre de l'historique sont l'autorité
// de l'API : rien n'est recalculé.
//
// LE SEUL DÉRIVÉ AUTORISÉ est le renfort « Attente 48 h+ » (48 heures CALENDAIRES depuis `reopenedAt`,
// décision 4) — un PRÉDICAT DE LECTURE, jamais un cinquième état.
//
// VERROUILLAGE (précision GATE_HUMAN du 2026-09-18) : il n'existe aucun verrou par couple. Une ligne
// AFFICHE un couple Fournisseur × Profil × Séniorité, mais l'état est porté par le FOURNISSEUR : toutes
// ses lignes basculent ensemble. Les agrégats d'ici comptent donc systématiquement les DEUX grains
// (lignes ET fournisseurs distincts), pour que les libellés puissent nommer celui qui agit.

/** Les 4 états métier ACTUELS (décision 3 : statuts INCHANGÉS). Ordre d'affichage des menus de filtre. */
export const NEGOTIATION_STATE_ORDER: readonly NegotiationEntryStateDto[] = [
  'ModifAutorisee',
  'EnCoursModif',
  'Modifie',
  'Verrouille',
];

/** Libellé FR de chaque état — porteur de l'information (WCAG 1.4.1 : jamais la couleur seule). */
export const NEGOTIATION_STATE_LABEL: Readonly<
  Record<NegotiationEntryStateDto, string>
> = {
  ModifAutorisee: 'Modification autorisée',
  EnCoursModif: 'En cours de modification',
  Modifie: 'Modifié',
  Verrouille: 'Verrouillé',
};

/** Habillage du badge d'état (contrastes ≥ 4.5:1 : famille-900 sur famille-100). Renfort SEULEMENT. */
export const NEGOTIATION_STATE_BADGE_CLASS: Readonly<
  Record<NegotiationEntryStateDto, string>
> = {
  ModifAutorisee: 'border-sky-300 bg-sky-100 text-sky-900',
  EnCoursModif: 'border-amber-300 bg-amber-100 text-amber-900',
  Modifie: 'border-emerald-300 bg-emerald-100 text-emerald-900',
  Verrouille: 'border-neutral-300 bg-neutral-100 text-neutral-900',
};

/** Seuil du RENFORT d'affichage « Attente 48 h+ » : 48 heures CALENDAIRES (décision 4, sans jours ouvrés). */
export const AWAITING_THRESHOLD_MS = 48 * 60 * 60 * 1000;

/** Clé de ligne STABLE (Fournisseur × Profil × Séniorité) — clé de rendu et clé de sélection. */
export function lineKey(line: NegotiationLineDto): string {
  return `${line.supplierSiret}::${line.profileCode}::${line.seniorityLevel}`;
}

/** Libellé d'affichage d'un fournisseur : raison sociale si connue, sinon SIRET (dégradation tolérante). */
export function supplierLabelOf(line: {
  readonly raisonSociale?: string;
  readonly supplierSiret: string;
}): string {
  return line.raisonSociale ?? line.supplierSiret;
}

/** Libellé court d'un couple profil × niveau (« FR-3.01 Expert ») — unité de lecture de l'historique. */
export function coupleLabel(ref: {
  readonly profileCode: string;
  readonly seniorityLevel: NegotiationLineDto['seniorityLevel'];
}): string {
  return `${ref.profileCode} ${seniorityLabel(ref.seniorityLevel)}`;
}

/**
 * Une ligne est « en attente » quand la négociation est ouverte et qu'AUCUNE re-saisie n'a encore été
 * faite (état `ModifAutorisee`). C'est la même définition que l'indicateur de progression DBU l.1267
 * (« en attente de modification »), afin qu'un même mot ne compte pas deux populations différentes.
 */
export function isPendingLine(line: NegotiationLineDto): boolean {
  return line.state === 'ModifAutorisee';
}

/**
 * RENFORT d'affichage « Attente 48 h+ » : ligne en attente dont l'ouverture remonte à 48 heures
 * calendaires ou plus. Jamais un état — l'état reste l'un des 4 relayés par l'API.
 */
export function isAwaitingOver48h(line: NegotiationLineDto, nowMs: number): boolean {
  if (!isPendingLine(line) || line.reopenedAt === null) {
    return false;
  }
  const openedMs = Date.parse(line.reopenedAt);
  if (Number.isNaN(openedMs)) {
    return false;
  }
  return nowMs - openedMs >= AWAITING_THRESHOLD_MS;
}

/** Agrégats de la ligne des 4 indicateurs (aucune valeur métier recalculée : ce sont des comptes). */
export interface NegotiationIndicators {
  /** Lignes NON verrouillées (négociations ouvertes). */
  readonly openLineCount: number;
  readonly openSupplierCount: number;
  readonly openProfileCount: number;
  /** SIRET distincts des fournisseurs encore ouverts — cible du verrouillage global. */
  readonly openSirets: readonly string[];
  /** Lignes portant une re-saisie effective / total des lignes de la campagne. */
  readonly resaisiCount: number;
  readonly totalLineCount: number;
  readonly lastResaisiAt: string | null;
  /** Lignes en attente (ouvertes, sans re-saisie) et sous-ensemble au-delà de 48 h. */
  readonly pendingCount: number;
  readonly pendingOver48hCount: number;
  /** Lignes verrouillées + dernier verrouillage horodaté. */
  readonly lockedLineCount: number;
  readonly lockedSupplierCount: number;
  readonly lastLockedAt: string | null;
}

/** Retient le plus RÉCENT de deux horodatages ISO 8601 (comparaison lexicographique fidèle en UTC). */
function latest(current: string | null, candidate: string | null): string | null {
  if (candidate === null) {
    return current;
  }
  if (current === null) {
    return candidate;
  }
  return candidate > current ? candidate : current;
}

/** Construit les 4 indicateurs de la ligne de tête à partir des lignes relayées par l'API. */
export function buildIndicators(
  lines: readonly NegotiationLineDto[],
  nowMs: number,
): NegotiationIndicators {
  const openSirets = new Set<string>();
  const openProfiles = new Set<string>();
  const lockedSirets = new Set<string>();
  let openLineCount = 0;
  let resaisiCount = 0;
  let pendingCount = 0;
  let pendingOver48hCount = 0;
  let lockedLineCount = 0;
  let lastResaisiAt: string | null = null;
  let lastLockedAt: string | null = null;

  for (const line of lines) {
    if (line.lockedFinal) {
      lockedLineCount += 1;
      lockedSirets.add(line.supplierSiret);
      lastLockedAt = latest(lastLockedAt, line.lockedAt);
    } else {
      openLineCount += 1;
      openSirets.add(line.supplierSiret);
      openProfiles.add(line.profileCode);
    }
    if (line.resaisiAt !== null || line.tjmResaisiEur !== null) {
      resaisiCount += 1;
      lastResaisiAt = latest(lastResaisiAt, line.resaisiAt);
    }
    if (isPendingLine(line)) {
      pendingCount += 1;
      if (isAwaitingOver48h(line, nowMs)) {
        pendingOver48hCount += 1;
      }
    }
  }

  return {
    openLineCount,
    openSupplierCount: openSirets.size,
    openProfileCount: openProfiles.size,
    openSirets: [...openSirets],
    resaisiCount,
    totalLineCount: lines.length,
    lastResaisiAt,
    pendingCount,
    pendingOver48hCount,
    lockedLineCount,
    lockedSupplierCount: lockedSirets.size,
    lastLockedAt,
  };
}

/**
 * Indicateur de progression DBU l.1267 — « X fournisseurs en attente de modification sur Y autorisés ».
 * CONSERVÉ au grain FOURNISSEUR (phrase et assertion ST inchangées) alors que le tableau passe au grain
 * ligne : la phrase parle de fournisseurs, elle continue donc de les compter.
 */
export interface NegotiationProgress {
  readonly awaitingSuppliers: number;
  readonly authorizedSuppliers: number;
}

export function buildProgress(
  lines: readonly NegotiationLineDto[],
): NegotiationProgress {
  const authorized = new Set<string>();
  const awaiting = new Set<string>();
  for (const line of lines) {
    authorized.add(line.supplierSiret);
    if (isPendingLine(line)) {
      awaiting.add(line.supplierSiret);
    }
  }
  return { awaitingSuppliers: awaiting.size, authorizedSuppliers: authorized.size };
}

/**
 * Filtres de la barre (état de vue d'URL).
 *
 * LOT F1 (GATE_HUMAN 2026-09-21, « sélection multiple par cases à cocher ») : fournisseur, profil et
 * état sont des LISTES. Liste absente OU VIDE = filtre non posé, donc « tous » — aucune sentinelle.
 */
export interface NegotiationFilters {
  readonly supplierSirets?: readonly string[] | undefined;
  readonly profileCodes?: readonly string[] | undefined;
  readonly states?: readonly NegotiationEntryStateDto[] | undefined;
  /** Case « Afficher les verrouillées » : `false` ⇒ les lignes verrouillées sont masquées. */
  readonly showLocked: boolean;
}

/** Liste absente ou vide ⇒ le filtre n'est pas posé, tout passe (OU logique à l'intérieur du filtre). */
function passesList<T extends string>(
  values: readonly T[] | undefined,
  candidate: T,
): boolean {
  return values === undefined || values.length === 0 || values.includes(candidate);
}

/**
 * Applique les filtres d'affichage : ET logique ENTRE filtres, OU logique DANS un filtre. La case
 * « Afficher les verrouillées » ne s'applique PAS quand l'utilisateur a explicitement demandé l'état
 * « Verrouillé » dans le menu : il verrait sinon une liste vide sans comprendre pourquoi. En
 * multi-valeurs, la même règle vaut dès que « Verrouillé » figure PARMI les états cochés.
 */
export function filterLines(
  lines: readonly NegotiationLineDto[],
  filters: NegotiationFilters,
): readonly NegotiationLineDto[] {
  const asksLocked = filters.states?.includes('Verrouille') === true;
  return lines.filter((line) => {
    if (!passesList(filters.supplierSirets, line.supplierSiret)) {
      return false;
    }
    if (!passesList(filters.profileCodes, line.profileCode)) {
      return false;
    }
    if (!passesList(filters.states, line.state)) {
      return false;
    }
    if (!filters.showLocked && !asksLocked && line.lockedFinal) {
      return false;
    }
    return true;
  });
}

/** Option de menu de filtre (valeur technique + libellé affiché). */
export interface FilterOption {
  readonly value: string;
  readonly label: string;
}

/** Fournisseurs présents dans les lignes, triés par libellé (raison sociale à défaut SIRET). */
export function supplierOptions(
  lines: readonly NegotiationLineDto[],
): readonly FilterOption[] {
  const byS: Map<string, string> = new Map();
  for (const line of lines) {
    if (!byS.has(line.supplierSiret)) {
      byS.set(line.supplierSiret, supplierLabelOf(line));
    }
  }
  return [...byS.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
}

/** Profils présents dans les lignes, triés par code (libellé du catalogue figé quand il existe). */
export function profileOptions(
  lines: readonly NegotiationLineDto[],
): readonly FilterOption[] {
  const byP: Map<string, string> = new Map();
  for (const line of lines) {
    if (!byP.has(line.profileCode)) {
      byP.set(
        line.profileCode,
        line.profileLabel !== undefined
          ? `${line.profileCode} — ${line.profileLabel}`
          : line.profileCode,
      );
    }
  }
  return [...byP.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.value.localeCompare(b.value, 'fr'));
}

/** SIRET distincts d'un ensemble de lignes — la sélection agit au grain FOURNISSEUR. */
export function distinctSirets(
  lines: readonly NegotiationLineDto[],
): readonly string[] {
  return [...new Set(lines.map((line) => line.supplierSiret))];
}

// -----------------------------------------------------------------------------
// Formats (typographie FR) — aucune valeur n'est transformée, seulement présentée.
// -----------------------------------------------------------------------------

/** Groupe les milliers avec une espace fine insécable (U+202F), typographie FR : « 1 150 ». */
function groupDigits(absolute: number): string {
  return String(Math.abs(Math.trunc(absolute))).replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ' ',
  );
}

/** Montant entier en euros (« 1 150 € ») ; `null` ⇒ « — ». */
export function formatEurAmount(value: number | null): string {
  if (value === null) {
    return '—';
  }
  const sign = value < 0 ? '−' : '';
  return `${sign}${groupDigits(value)} €`;
}

/**
 * Écart SIGNÉ explicite (« +50 € », « −30 € », « ±0 € ») ; `null` ⇒ « — ». Le SIGNE porte l'information,
 * jamais la couleur seule (WCAG 1.4.1) — l'écran ajoute un libellé lisible par lecteur d'écran.
 */
export function formatSignedEur(value: number | null): string {
  if (value === null) {
    return '—';
  }
  if (value === 0) {
    return '±0 €';
  }
  return value > 0
    ? `+${groupDigits(value)} €`
    : `−${groupDigits(value)} €`;
}

/** Horodatage ISO 8601 → date + heure FR (« 27/06/2026 12:00 ») ; `null` ⇒ « — ». */
export function formatDateTimeFr(iso: string | null): string {
  if (iso === null) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  // Fuseau de L'UTILISATEUR (cohérence avec le reste des écrans admin) ; déterminisme des tests figé
  // par `TZ` (vitest.config).
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

/** Horodatage ISO 8601 → « JJ/MM hh:mm » (sous-textes des indicateurs) ; `null` ⇒ « — ». */
export function formatDayTimeFr(iso: string | null): string {
  if (iso === null) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  const day = new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
  }).format(date);
  const time = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  return `${day} ${time}`;
}

/** Accorde un nom au pluriel (« 1 ligne » / « 4 lignes »). */
export function plural(count: number, singular: string, pluralForm?: string): string {
  const word = count > 1 ? (pluralForm ?? `${singular}s`) : singular;
  return `${String(count)} ${word}`;
}

// -----------------------------------------------------------------------------
// Historique de négociation (lot N3) — l'API rend des DONNÉES, l'UI compose les PHRASES.
// -----------------------------------------------------------------------------
// Ce que l'API NE FOURNIT PAS et qui n'est donc PAS affiché (rapport DEV_API-N3, §A-1/A-2) :
//   • l'ORIGINE de l'ouverture (« depuis Analyse ») — aucune colonne ne la porte ;
//   • la NOTIFICATION du fournisseur (« fournisseur notifié ») — aucun horodatage n'existe.
// Les inventer serait afficher une information qu'aucune donnée ne soutient.

/** Onglets de l'historique (maquette) : « Relance » n'a pas d'onglet et n'apparaît que sous « Tous ». */
export interface NegotiationHistoryTab {
  readonly id: NegotiationHistoryFilterDto;
  readonly label: string;
}

export const NEGOTIATION_HISTORY_TABS: readonly NegotiationHistoryTab[] = [
  { id: 'Tous', label: 'Tous' },
  { id: 'Ouverture', label: 'Ouvertures' },
  { id: 'Resaisie', label: 'Re-saisies' },
  { id: 'Verrouillage', label: 'Verrouillages' },
];

/** Pastille de nature d'entrée : renfort DÉCORATIF, le libellé composé porte l'information. */
export const NEGOTIATION_HISTORY_DOT_CLASS: Readonly<
  Record<NegotiationHistoryEntryDto['type'], string>
> = {
  Ouverture: 'bg-sky-500',
  Resaisie: 'bg-emerald-500',
  Verrouillage: 'bg-neutral-500',
  Relance: 'bg-dash-late',
};

/** Nom de l'acteur d'une entrée (l'API ne transporte aucune identité nominative d'administrateur). */
function actorName(entry: NegotiationHistoryEntryDto): string {
  switch (entry.actorKind) {
    case 'Admin':
      return 'Administrateur';
    case 'Fournisseur':
      return supplierLabelOf(entry);
    default:
      return 'Le système';
  }
}

/** Phrase composée d'une entrée : une ligne de tête + d'éventuels détails par couple. */
export interface NegotiationHistoryText {
  readonly headline: string;
  readonly details: readonly string[];
}

/** Détail chiffré d'un verrouillage, couple par couple (valeur RETENUE + comparaison au TJM C2). */
function lockDetail(ref: NegotiationHistoryLineRefDto): string {
  const retained = `TJM final ${formatEurAmount(ref.tjmFinalEur)}`;
  if (ref.tjmResaisiEur === null) {
    return `${coupleLabel(ref)} — ${retained} (sans re-saisie, TJM C2 conservé)`;
  }
  const comparison =
    ref.ecartEur === null
      ? 're-saisie acceptée'
      : `re-saisie acceptée, ${formatSignedEur(ref.ecartEur)} vs C2`;
  return `${coupleLabel(ref)} — ${retained} (${comparison})`;
}

/** Détail chiffré d'une re-saisie (« 1 150 € → 1 120 € (−30 €) »). */
function resaisieDetail(ref: NegotiationHistoryLineRefDto): string {
  if (ref.tjmC2Eur === null) {
    return `${formatEurAmount(ref.tjmResaisiEur)}`;
  }
  const ecart = ref.ecartEur === null ? '' : ` (${formatSignedEur(ref.ecartEur)})`;
  return `${formatEurAmount(ref.tjmC2Eur)} → ${formatEurAmount(ref.tjmResaisiEur)}${ecart}`;
}

/**
 * Compose le libellé CHIFFRÉ d'une entrée d'historique à partir des SEULES données de l'API (type,
 * acteur, raison sociale, couples et montants). Une entrée portant plusieurs couples (le verrouillage
 * est au grain FOURNISSEUR) rend une ligne de tête + un détail par couple, plutôt qu'une phrase
 * interminable.
 */
export function historyEntryText(
  entry: NegotiationHistoryEntryDto,
): NegotiationHistoryText {
  const supplier = supplierLabelOf(entry);
  const who = actorName(entry);

  switch (entry.type) {
    case 'Ouverture': {
      const couples = entry.lines.map(coupleLabel).join(', ');
      return {
        headline:
          couples === ''
            ? `${who} a ouvert la négociation de ${supplier}`
            : `${who} a ouvert ${supplier} · ${couples}`,
        details: [],
      };
    }
    case 'Resaisie': {
      const first = entry.lines[0];
      if (first === undefined) {
        return { headline: `${supplier} a re-saisi ses tarifs`, details: [] };
      }
      return {
        headline: `${supplier} a re-saisi ${coupleLabel(first)} — ${resaisieDetail(first)}`,
        details: entry.lines.slice(1).map((ref) => `${coupleLabel(ref)} — ${resaisieDetail(ref)}`),
      };
    }
    case 'Verrouillage': {
      const first = entry.lines[0];
      if (first === undefined) {
        return { headline: `${who} a verrouillé la négociation de ${supplier}`, details: [] };
      }
      if (entry.lines.length === 1) {
        return { headline: `${who} a verrouillé ${supplier} · ${lockDetail(first)}`, details: [] };
      }
      return {
        headline: `${who} a verrouillé ${supplier} · ${plural(entry.lines.length, 'ligne')}`,
        details: entry.lines.map(lockDetail),
      };
    }
    default:
      return {
        headline:
          entry.actorKind === 'Systeme'
            ? `Relance automatique envoyée à ${supplier}`
            : `${who} a relancé ${supplier}`,
        details: [],
      };
  }
}
